import {
  faBoxArchive,
  faCheckCircle,
  faCopy,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import { DisplayDateTime } from "src/base-components/Date";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { MENU_DIVIDER } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import { useCaseTemplateWithVersions } from "src/cases/queries/templates";
import { TemplateVersionName } from "src/cases/templates/TemplateVersionName";
import {
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel as CaseTemplateVersion,
  CaseTemplateVersionPublishState,
} from "src/clients/case-management";
import { ExcludesFalse } from "src/flow/types";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useMaxCaseTemplateVersions } from "src/hooks/useMaxCaseTemplateVersions";
import { WhitePane } from "src/jobs/common/WhitePane";
import {
  CaseTemplateVersionsPageParams,
  getUrlToCaseTemplateVersionPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const helper = createColumnHelper<CaseTemplateVersion>();

const COLUMNS = [
  helper.accessor("attributes.version_name", {
    header: (info) => {
      const isDraftTable =
        info.table.getRowModel().rows.at(0)?.original.attributes
          .publish_state === CaseTemplateVersionPublishState.DRAFT;
      return (
        <Header info={info}>
          <div className="pl-5">{isDraftTable ? "Draft" : "Published"}</div>
        </Header>
      );
    },
    cell: (info) => (
      <Cell className="pl-5" info={info}>
        <TemplateVersionName
          name={info.getValue()}
          state={info.row.original.attributes.publish_state}
        />
      </Cell>
    ),
  }),
  helper.accessor("attributes.created_at", {
    header: (info) => <Header info={info}>Created at</Header>,
    cell: (info) => (
      <Cell info={info}>
        {info.getValue() ? <DisplayDateTime value={info.getValue()} /> : "-"}
      </Cell>
    ),
    size: 300,
  }),
  helper.accessor("attributes.updated_at", {
    header: (info) => <Header info={info}>Updated at</Header>,
    cell: (info) => (
      <Cell info={info}>
        {info.getValue() ? <DisplayDateTime value={info.getValue()} /> : "-"}
      </Cell>
    ),
    size: 300,
  }),
  helper.display({
    id: "actions",
    cell: ({ row, table }) => {
      if (!table.options.meta?.actions) return null;

      return (
        <CaseTemplateVersionActionsMenu
          version={row.original}
          onArchive={table.options.meta?.actions.onArchive}
          onDuplicate={table.options.meta?.actions.onDuplicate}
          onPublish={table.options.meta?.actions.onPublish}
        />
      );
    },
    size: 100,
  }),
];

const CaseTemplateVersionActionsMenu: React.FC<{
  version: CaseTemplateVersion;
  onDuplicate: (version: CaseTemplateVersion) => void;
  onArchive: (version: CaseTemplateVersion) => void;
  onPublish: (version: CaseTemplateVersion) => void;
}> = ({ version, onDuplicate, onArchive, onPublish }) => {
  const { templateId } = useParamsDecode<CaseTemplateVersionsPageParams>();
  const { caseTemplates } = useCapabilities();
  const { data: template } = useCaseTemplateWithVersions(templateId);
  const maxVersionsPerTemplate = useMaxCaseTemplateVersions();

  const templateActiveVersions =
    template?.versions?.filter((v) => v.attributes.archived === false) ?? [];

  const canArchivePublished =
    version.attributes.publish_state !==
      CaseTemplateVersionPublishState.PUBLISHED ||
    caseTemplates.canArchivePublished;

  const getDuplicateDisabledReason = (() => {
    if (!caseTemplates.canCreateVersion) {
      return "Only workspace admins can create template versions";
    }
    if (templateActiveVersions.length >= maxVersionsPerTemplate) {
      return `You've reached the limit of ${maxVersionsPerTemplate} versions for this template. To create a new version, please delete an existing one.`;
    }
    return undefined;
  })();

  const options = [
    {
      key: "Duplicate",
      icon: faCopy,
      action: () => onDuplicate(version),
      disabled: getDuplicateDisabledReason,
    },
    version.attributes.publish_state ===
      CaseTemplateVersionPublishState.DRAFT && MENU_DIVIDER,
    version.attributes.publish_state ===
      CaseTemplateVersionPublishState.DRAFT && {
      key: "Publish",
      icon: faCheckCircle,
      action: () => onPublish(version),
    },
    canArchivePublished && MENU_DIVIDER,
    canArchivePublished && {
      key: "Archive",
      icon: faBoxArchive,
      action: () => onArchive(version),
      disabled:
        templateActiveVersions.length <= 1
          ? "You cannot archive the last version"
          : undefined,
    },
  ].filter(Boolean as unknown as ExcludesFalse);

  return (
    <div className="flex items-end justify-end pr-5">
      <EllipsisOptionsDropdown
        buttonClassName="text-gray-300 w-5 opacity-0 group-hover:opacity-100 data-[menu-open=true]:opacity-100"
        dataLoc="case-template-version-actions"
        elements={options}
        ellipsisDirection="vertical"
        timeoutDuration={200}
      />
    </div>
  );
};

export const CaseTemplateVersionsTable: React.FC<{
  versions: CaseTemplateVersion[];
  isLoading: boolean;
  onDuplicate: (version: CaseTemplateVersion) => void;
  onArchive: (version: CaseTemplateVersion) => void;
  onPublish: (version: CaseTemplateVersion) => void;
}> = ({
  versions,
  isLoading,

  onDuplicate,
  onArchive,
  onPublish,
}) => {
  const { orgId, wsId, templateId } =
    useParamsDecode<CaseTemplateVersionsPageParams>();
  const navigate = useNavigate();

  return (
    <WhitePane>
      <Table
        actions={{
          onDuplicate,
          onArchive,
          onPublish,
        }}
        columns={COLUMNS}
        data={versions}
        dataLoc="case-template-versions-table"
        frameClassName="w-full"
        isLoading={isLoading}
        loadingRowsCount={3}
        rowClassName="cursor-pointer hover:bg-gray-50"
        rowPropsGetter={(row) => ({
          "data-loc": `case-template-version-row-${row.original.attributes.version_name}`,
          onClick: () => {
            navigate(
              getUrlToCaseTemplateVersionPage(
                orgId,
                wsId,
                templateId,
                row.original.id,
              ),
            );
          },
        })}
      />
    </WhitePane>
  );
};
